import type { ethers } from "ethers";
import abi from "./lifespan_wls_abi.json";
import { INFURA_ID } from "./tomorrow_utils";
import { MinusPlusButtonArgs, MinusPlusButton } from "./setup";
import { TitleElementUpdater, CostElementUpdater } from "./tomorrow_components";
import { postToWebsite } from "./upload_message";
import { getUtmCampaign, getUtmCodes, hasGclid, logPageVisitToReddit, logPurchaseToReddit } from "./utils";

// IDs that appear once in the webpage
const CONNECT_META_MASK_BUTTON_ID = "web3-connectMetamask";
const CONNECT_WALLET_CONNECT_BUTTON_ID = "web3-connectWalletConnect";
const CONNECT_COINBASE_WALLET_BUTTON_ID = "web3-connectCoinbaseWallet";

const CONNECT_WALLET_POPUP_ID = "web3-connectWallet";
const TRANSACTION_ERROR_ID = "web3-transactionError";
const TRANSACTION_ERROR_MESSAGE_ID = "web3-errorMessage";

// Loading boxes
const READY_BOX_ID = "web3-ready";
const LOADING_BOX_ID = "web3-loading";

const WHITELIST_CONTRACT_ADDRESS: string =
  "0xfffe635988b59039c1e6cc59fcadb6a9ab159d6d";
const WHITELIST_CHAIN_ID = 1;

import {
  setStyleToNone,
  setOpacity,
  setStyleToFlex,
  addHideOnEscapeKey,
  setMessage,
  setDisabled,
} from "./setup";
import { logEvent, logToTwitter } from "./utils";

const SUBMIT_BUTTON_ID: string = "web3-submit";
const OUT_OF_SPOTS_ID: string = "web3-noMoreSpots";
const VOTES_TITLE_ID: string = "web3-votesTitle";
const COST_ID: string = "web3-cost";

interface RadioQuestion {
  errorId: string;
  radioIds: string[];
  textId?: string;
}

const QUESTIONS: RadioQuestion[] = [
  {
    errorId: "Q1-error",
    radioIds: ["Q1A1", "Q1A2", "Q1A3", "Q1A4", "Q1A5", "Q1A6", "Q1A7"],
    textId: "Q1-text",
  },
  {
    errorId: "Q2-error",
    radioIds: ["Q2A1", "Q2A2", "Q2A3", "Q2A4"],
    textId: "Q2-text",
  },
  {
    errorId: "Q3-error",
    radioIds: ["Q3A1", "Q3A2", "Q3A3", "Q3A4"],
  },
  {
    errorId: "Q4-error",
    radioIds: ["Q4A1", "Q4A2", "Q4A3", "Q4A4", "Q4A5"],
  },
];

const GREPPER_STRING: string = "lifespan_fewgf";

const MINUS_PLUS_BUTTON_ARGS: MinusPlusButtonArgs = {
  minusButtonId: "web3-minusButton",
  minusButtonDisabledId: "web3-minusButtonDisabled",
  plusButtonId: "web3-plusButton",
  plusButtonDisabledId: "web3-plusButtonDisabled",
  numId: "web3-numToMintId",
  min: 1,
  max: 20,
  default: 10,
};

function getAnswer(ids: string[]): number {
  for (let i = 0; i < ids.length; ++i) {
    const id = ids[i];
    const element = document.getElementById(id) as HTMLInputElement;
    if (element?.checked) {
      return i;
    }
  }
  return -1;
}

function clearErrors(questionIds: RadioQuestion[]) {
  for (let questionId of questionIds) {
    setOpacity(questionId.errorId, "0");
  }
}

function getAnswersAndSetErrors(questionIds: RadioQuestion[]): number[] {
  let results = [];
  for (let questionId of questionIds) {
    const answer = getAnswer(questionId.radioIds);
    results.push(answer);
    if (answer < 0) {
      setOpacity(questionId.errorId, "100");
    }
  }
  return results;
}

function getErrorMessage(errorMessageIn: string): string {
  console.log(errorMessageIn);
  if (errorMessageIn.includes("Address has already voted.")) {
    return "You have already voted.";
  } else if (errorMessageIn.includes("Too many votes have been cast.")) {
    return "You are trying to claim more whitespots than are left or all whitelist spots are gone.";
  } else if (errorMessageIn.includes("User denied transaction")) {
    return "You denied the transaction.";
  } else {
    return "Transaction not executed, please refresh page to try again. You may have insufficient funds or you rejected the request or the wallet may have gotten disconnected.";
  }
}

function getText(): string {
  let output_string = "";
  for (let question of QUESTIONS) {
    if (question.textId) {
      output_string += question.textId;
      const element = document.getElementById(
        question.textId
      ) as HTMLInputElement;
      output_string += "+" + element.value.substring(0, 280) + "+";
    }
  }
  const Q5textid: string = "Q5-text";
  output_string += Q5textid;
  const element = document.getElementById(Q5textid) as HTMLInputElement;
  output_string += "+" + element.value.substring(0, 280) + "+";
  return output_string;
}

function getContract(
  ethers: any,
  signer: ethers.providers.JsonRpcSigner | ethers.providers.InfuraProvider
): ethers.Contract {
  return new ethers.Contract(
    WHITELIST_CONTRACT_ADDRESS,
    new ethers.utils.Interface(abi),
    signer
  );
}

const listenerNoAccount = (event: any) => {
  event.preventDefault();
  logEvent("LIFE_WLS_vbp_no_wallet");
  clearErrors(QUESTIONS);
  const answers = getAnswersAndSetErrors(QUESTIONS);
  const invalidAnswerExists = answers.some((x) => x < 0);
  if (invalidAnswerExists) return;
  clearErrors(QUESTIONS);
  logEvent("LIFE_WLS_vbp_no_wallet_survey_filled");
  setStyleToFlex(CONNECT_WALLET_POPUP_ID);
};

interface GenerateListenerWithAccountArgs {
  contract: ethers.Contract;
  minusPlusButton: MinusPlusButton;
  costComputer: CostComputer;
  titleElementUpdater: TitleElementUpdater;
}

function generateListenerWithAccount(
  args: GenerateListenerWithAccountArgs
): EventListener {
  return async (event) => {
    event.preventDefault();
    logEvent("LIFE_WLS_vbp_wallet");
    clearErrors(QUESTIONS);
    const answers = getAnswersAndSetErrors(QUESTIONS);
    const invalidAnswerExists = answers.some((x) => x < 0);
    if (invalidAnswerExists) return;
    clearErrors(QUESTIONS);
    const promoCode = getUtmCampaign();
    console.log(promoCode);
    setStyleToNone(READY_BOX_ID);
    setStyleToFlex(LOADING_BOX_ID);
    logEvent("LIFE_WLS_vbp_wallet_survey_filled");
    let numVotesToCast = args.minusPlusButton.getNum();
    let cost = args.costComputer.compute(numVotesToCast);
    console.log(cost);
    args.contract
      .vote(answers, numVotesToCast, promoCode, { value: cost })
      .then(
        (transaction: any) => {
          logEvent("LIFE_WLS_vote_submitted");
          transaction.wait().then(async () => {
            args.costComputer.totalVotesCast += numVotesToCast;
            args.titleElementUpdater.onTotalVotesCastChanged(
              args.costComputer.totalVotesCast
            );
            if (args.minusPlusButton.onNumFieldChangedCallback) {
              args.minusPlusButton.onNumFieldChangedCallback(
                args.minusPlusButton.getNum()
              );
            }
            setStyleToNone(LOADING_BOX_ID);
            setStyleToFlex(READY_BOX_ID);

            logEvent("LIFE_WLS_vote_succeeded");
            logEvent("LIFE_WLS_vote_" + promoCode);
            logToTwitter(5, 1.0);
            logPurchaseToReddit(5, 1.0);
            postToWebsite(
              new Map<string, string>([
                ["grepper", GREPPER_STRING],
                ["address", await args.contract.signer.getAddress()],
                ["answers", getText()],
                ["utm", getUtmCodes()],
                ["has_gclid", hasGclid()],
              ])
            ).then((value) => {
              console.log(value);
            });
          });
        },
        (error: any) => {
          setStyleToNone(LOADING_BOX_ID);
          setStyleToFlex(READY_BOX_ID);
          setStyleToFlex(TRANSACTION_ERROR_ID);
          setMessage(
            TRANSACTION_ERROR_MESSAGE_ID,
            getErrorMessage(error.data?.message ?? error.message)
          );
        }
      );
  };
}

function getInfuraProvider(
  ethers: any,
  infuraId: string
): ethers.providers.InfuraProvider {
  let infuraProvider = new ethers.providers.InfuraProvider(
    WHITELIST_CHAIN_ID,
    infuraId
  );
  infuraProvider.pollingInterval = 15000;
  return infuraProvider;
}

function getInfuraContract(ethers: any, infuraId: string): ethers.Contract {
  const infuraProvider = getInfuraProvider(ethers, infuraId);
  return getContract(ethers, infuraProvider);
}

interface onSignerAvailableArgs {
  ethers: any;
  infuraContract: ethers.Contract;
  signer: ethers.providers.JsonRpcSigner;
  minusPlusButton: MinusPlusButton;
  titleElementUpdater: TitleElementUpdater;
  costComputer: CostComputer;
}

/** Sets up buttons to call  */
function onSignerAvailable(args: onSignerAvailableArgs): Promise<number> {
  let button = document.getElementById(SUBMIT_BUTTON_ID);
  if (!button) return Promise.resolve(0);
  let contract = getContract(args.ethers, args.signer);
  let totalVotesCastPromise = args.signer
    .getAddress()
    .then((address): Promise<ethers.BigNumber> => {
      console.log(address);
      return args.infuraContract.voters(address);
    })
    .then((value: ethers.BigNumber): number => {
      return value.toNumber();
    });
  button?.removeEventListener("click", listenerNoAccount);
  button?.addEventListener(
    "click",
    generateListenerWithAccount({
      contract: contract,
      minusPlusButton: args.minusPlusButton,
      costComputer: args.costComputer,
      titleElementUpdater: args.titleElementUpdater,
    })
  );
  return totalVotesCastPromise;
}

async function getRemainingVotes(contract: ethers.Contract): Promise<number> {
  const data = await Promise.all([contract.maxVotes(), contract.totalVotes()]);
  return data[0] - data[1];
}

function getExtraCostPerVote(
  contract: ethers.Contract
): Promise<ethers.BigNumber> {
  return contract.extraVoteCost();
}

function updateRemaining(
  titleElementUpdater: TitleElementUpdater,
  remainingVotes: number
) {
  titleElementUpdater.onSpotsRemainingChanged(remainingVotes);
  if (remainingVotes <= 0) {
    setStyleToNone(SUBMIT_BUTTON_ID);
    setStyleToFlex(OUT_OF_SPOTS_ID);
  }
}

function updateUiIfAlreadyVoted(
  titleElementUpdater: TitleElementUpdater,
  votesCast: number
) {
  if (votesCast > 0) {
    titleElementUpdater.onTotalVotesCastChanged(votesCast);
  }
}

class CostComputer {
  totalVotesCast: number;
  costPerExtraVote: ethers.BigNumber;

  constructor(totalVotesCast: number, costPerExtraVote: ethers.BigNumber) {
    this.totalVotesCast = totalVotesCast;
    this.costPerExtraVote = costPerExtraVote;
  }

  compute(numVotesToCast: number) {
    if (this.totalVotesCast > 0) {
      return this.costPerExtraVote.mul(numVotesToCast);
    }
    return this.costPerExtraVote.mul(numVotesToCast - 1);
  }
}

function costString(
  ethers: any,
  costComputer: CostComputer,
  numVotesToCast: number
): string {
  let cost = costComputer.compute(numVotesToCast);
  if (cost.eq(0)) {
    return "FREE";
  }
  return ethers.utils.formatEther(cost) + " ETH";
}

function logWeb3Available(wallets: any) {
  if (wallets.isInjectedWeb3Available()) {
    logEvent("LIFE_WLS_web3_available");
    logPageVisitToReddit();
  } else {
    logEvent("LIFE_WLS_web3_unavailable");
  }
}

export function main(infuraId: string) {
  setDisabled(OUT_OF_SPOTS_ID);
  let button = document.getElementById(SUBMIT_BUTTON_ID);
  if (!button) return;
  button?.addEventListener("click", listenerNoAccount);
  addHideOnEscapeKey([CONNECT_WALLET_POPUP_ID, TRANSACTION_ERROR_ID]);
  let minusPlusButton = new MinusPlusButton(MINUS_PLUS_BUTTON_ARGS);

  let allPromises: any[] = [];

  const walletsPromise = import("./wallets");
  allPromises.push(walletsPromise.then(logWeb3Available));

  const ethersPromise = import("ethers");

  let infuraContractPromise = ethersPromise.then((ethers) => {
    return getInfuraContract(ethers, infuraId);
  });
  let titleElementUpdater = new TitleElementUpdater(VOTES_TITLE_ID);
  let costElementUpdater = new CostElementUpdater(COST_ID);

  let extraVoteCostPromise = infuraContractPromise.then(getExtraCostPerVote);
  let costComputerPromise = extraVoteCostPromise.then((value) => {
    return new CostComputer(0, value);
  });

  // Get a signer if the wallet is already connected.
  let signerMaybePromise = walletsPromise.then(
    (wallets): Promise<ethers.providers.JsonRpcSigner | null> => {
      return wallets.getSignerIfConnected(infuraId);
    }
  );
  let numVotesAlreadyCastPromise = Promise.all([
    ethersPromise,
    infuraContractPromise,
    signerMaybePromise,
    costComputerPromise,
  ]).then(async (results): Promise<number> => {
    const [ethers, infuraContract, signerMaybe, costComputer] = results;
    setStyleToNone(CONNECT_WALLET_POPUP_ID);
    if (signerMaybe) {
      return onSignerAvailable({
        ethers: ethers,
        infuraContract: infuraContract,
        signer: signerMaybe,
        minusPlusButton: minusPlusButton,
        titleElementUpdater: titleElementUpdater,
        costComputer: costComputer,
      });
    }
    return Promise.resolve(0);
  });
  let updateOnVotedChangeFn = (value: number) => {
    updateUiIfAlreadyVoted(titleElementUpdater, value);
  };

  let updateOnRemainingChangeFn = (value: number) => {
    updateRemaining(titleElementUpdater, value);
  };

  // Get a signer when the user explicitly connects wallet in the current session.
  let signerPromise = walletsPromise.then(
    (wallets): Promise<ethers.providers.JsonRpcSigner> => {
      const metmaskProvider = wallets.getMetamaskProviderIfPresent();
      wallets.addReloadOnChainChangeAndDisconnect(metmaskProvider);
      wallets.requestChainChange(metmaskProvider, WHITELIST_CHAIN_ID);
      const coinbaseWalletProvider =
        wallets.getCoinbaseWalletProviderIfPresent();
      wallets.requestChainChange(coinbaseWalletProvider, WHITELIST_CHAIN_ID);

      return wallets.setupWallets(
        CONNECT_META_MASK_BUTTON_ID,
        CONNECT_WALLET_CONNECT_BUTTON_ID,
        CONNECT_COINBASE_WALLET_BUTTON_ID,
        infuraId
      );
    }
  );
  let totalVotesCastAfterConnectedPromise = Promise.all([
    ethersPromise,
    signerPromise,
    infuraContractPromise,
    costComputerPromise,
  ]).then((results) => {
    const [ethers, signer, infuraContract, costComputer] = results;
    setStyleToNone(CONNECT_WALLET_POPUP_ID);
    logEvent("LIFE_WLS_wallet_connected");
    return onSignerAvailable({
      ethers: ethers,
      infuraContract: infuraContract,
      signer: signer,
      minusPlusButton: minusPlusButton,
      titleElementUpdater: titleElementUpdater,
      costComputer: costComputer,
    });
  });
  allPromises.push(
    totalVotesCastAfterConnectedPromise.then(updateOnVotedChangeFn)
  );

  let uiBlockingPromises = [];

  let remainingVotesPromise = infuraContractPromise.then(getRemainingVotes);

  uiBlockingPromises.push(
    remainingVotesPromise.then(updateOnRemainingChangeFn)
  );
  uiBlockingPromises.push(
    numVotesAlreadyCastPromise.then(updateOnVotedChangeFn)
  );

  let callbackAssignedPromise = Promise.all([
    numVotesAlreadyCastPromise,
    ethersPromise,
    costComputerPromise,
  ]).then((results) => {
    let [totalVotesCast, ethers, costComputer] = results;
    costComputer.totalVotesCast = totalVotesCast;
    let callback = (numVotesToCast: number) => {
      costElementUpdater.updateText(
        costString(ethers, costComputer, numVotesToCast)
      );
    };
    callback(minusPlusButton.getNum());
    minusPlusButton.setOnNumFieldChangedCallback(callback);
  });
  uiBlockingPromises.push(callbackAssignedPromise);

  // The following only runs if the user connects their wallet in the current visit.
  Promise.all([
    totalVotesCastAfterConnectedPromise,
    ethersPromise,
    costComputerPromise,
    callbackAssignedPromise,
  ]).then((results) => {
    let [totalVotesCast, ethers, costComputer] = results;
    costComputer.totalVotesCast = totalVotesCast;
    let callback = (numVotesToCast: number) => {
      costElementUpdater.updateText(
        costString(ethers, costComputer, numVotesToCast)
      );
    };
    callback(minusPlusButton.getNum());
    minusPlusButton.setOnNumFieldChangedCallback(callback);
    button?.click();
  });

  allPromises.push(
    Promise.all(uiBlockingPromises).then((results) => {
      setStyleToNone(LOADING_BOX_ID);
      setStyleToFlex(READY_BOX_ID);
    })
  );

  allPromises.push(
    infuraContractPromise.then((contract) => {
      contract.on(
        "VoteOccurred",
        async function (address: string, remaining: ethers.BigNumber) {
          infuraContractPromise
            .then(getRemainingVotes)
            .then(updateOnRemainingChangeFn);
        }
      );
    })
  );
  return Promise.all(allPromises);
}

main(INFURA_ID);
